import React, { useState } from 'react';
import apiClient from '../../apiClient';
import { FaTimes, FaUpload, FaDownload, FaFileAlt, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import './BulkImportModal.css';

function BulkImportModal({ isOpen, onClose, apiBaseUrl }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        setSelectedFile(file);
        setError(null);
        setUploadResults(null);
      } else {
        setError('Please select a CSV file.');
        setSelectedFile(null);
      }
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        setSelectedFile(file);
        setError(null);
        setUploadResults(null);
      } else {
        setError('Please drop a CSV file.');
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get('/stakeholders/export-template');
      const { template, filename } = response.data;
      
      // Create and download the CSV file
      const blob = new Blob([template], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'stakeholder_import_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Failed to download template. Please try again.');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResults(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await apiClient.post('/stakeholders/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResults(response.data);
      setSelectedFile(null);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.error || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    const hasChanges = uploadResults && (uploadResults.created > 0 || uploadResults.updated > 0);
    onClose(hasChanges);
    
    // Reset state
    setSelectedFile(null);
    setUploadResults(null);
    setError(null);
    setDragOver(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content bulk-import-modal">
        <div className="modal-header">
          <h3>
            <FaUpload /> Bulk Import Stakeholders
          </h3>
          <button className="close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          {/* Instructions */}
          <div className="import-instructions">
            <h4>How to Import Stakeholders</h4>
            <ol>
              <li>Download the CSV template with the required columns</li>
              <li>Fill in your stakeholder data following the template format</li>
              <li>Upload your completed CSV file</li>
              <li>Review the import results and any errors</li>
            </ol>
          </div>

          {/* Template Download */}
          <div className="template-section">
            <h4>Step 1: Download Template</h4>
            <p>Download the CSV template to ensure your data is in the correct format.</p>
            <button 
              className="btn secondary"
              onClick={handleDownloadTemplate}
            >
              <FaDownload /> Download CSV Template
            </button>
          </div>

          {/* File Upload */}
          <div className="upload-section">
            <h4>Step 2: Upload Your CSV File</h4>
            
            <div 
              className={`file-drop-zone ${dragOver ? 'drag-over' : ''} ${selectedFile ? 'has-file' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="csv-file-input"
                disabled={uploading}
              />
              
              {selectedFile ? (
                <div className="selected-file">
                  <FaFileAlt className="file-icon" />
                  <div className="file-info">
                    <strong>{selectedFile.name}</strong>
                    <small>{(selectedFile.size / 1024).toFixed(2)} KB</small>
                  </div>
                  <button 
                    className="btn small secondary"
                    onClick={() => setSelectedFile(null)}
                    disabled={uploading}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="drop-zone-content">
                  <FaUpload className="upload-icon" />
                  <p>Drag and drop your CSV file here, or</p>
                  <label htmlFor="csv-file-input" className="btn primary">
                    Choose File
                  </label>
                  <small>Only CSV files are accepted</small>
                </div>
              )}
            </div>

            {selectedFile && (
              <div className="upload-actions">
                <button 
                  className="btn primary"
                  onClick={handleUpload}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Import Stakeholders'}
                </button>
              </div>
            )}
          </div>

          {/* Upload Results */}
          {uploadResults && (
            <div className="results-section">
              <h4>Import Results</h4>
              
              <div className="results-summary">
                <div className="result-stat success">
                  <FaCheckCircle />
                  <div>
                    <strong>{uploadResults.created}</strong>
                    <span>Created</span>
                  </div>
                </div>
                
                <div className="result-stat info">
                  <FaCheckCircle />
                  <div>
                    <strong>{uploadResults.updated}</strong>
                    <span>Updated</span>
                  </div>
                </div>
                
                {uploadResults.errors && uploadResults.errors.length > 0 && (
                  <div className="result-stat error">
                    <FaExclamationTriangle />
                    <div>
                      <strong>{uploadResults.errors.length}</strong>
                      <span>Errors</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="results-message">
                <p>{uploadResults.message}</p>
              </div>

              {uploadResults.errors && uploadResults.errors.length > 0 && (
                <div className="errors-list">
                  <h5>Errors encountered:</h5>
                  <ul>
                    {uploadResults.errors.map((error, index) => (
                      <li key={index} className="error-item">
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(uploadResults.created > 0 || uploadResults.updated > 0) && (
                <div className="success-message">
                  <FaCheckCircle />
                  <span>Import completed successfully! Your stakeholders have been added to the system.</span>
                </div>
              )}
            </div>
          )}

          {/* Field Mapping Guide */}
          <div className="field-guide-section">
            <h4>CSV Column Guide</h4>
            <div className="field-guide">
              <div className="field-category">
                <h5>Required Fields</h5>
                <ul>
                  <li><strong>name</strong> - Full name of the stakeholder</li>
                  <li><strong>email</strong> - Email address (must be unique)</li>
                </ul>
              </div>
              
              <div className="field-category">
                <h5>Optional Fields</h5>
                <ul>
                  <li><strong>job_position</strong> - Job title or position</li>
                  <li><strong>role</strong> - System role (Admin, Change Manager, Team Member)</li>
                  <li><strong>department</strong> - Department or division</li>
                  <li><strong>phone</strong> - Phone number</li>
                  <li><strong>preferred_contact_method</strong> - email, phone, teams, slack</li>
                  <li><strong>organization</strong> - Organization name (for external stakeholders)</li>
                  <li><strong>location</strong> - Physical location</li>
                  <li><strong>stakeholder_type</strong> - internal, external, vendor, client</li>
                  <li><strong>influence_level</strong> - high, medium, low</li>
                  <li><strong>interest_level</strong> - high, medium, low</li>
                  <li><strong>notes</strong> - Additional notes</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="tips-section">
            <h4>Tips for Successful Import</h4>
            <ul className="tips-list">
              <li>Ensure email addresses are unique - duplicate emails will update existing stakeholders</li>
              <li>Use the exact values shown in the guide for dropdowns (e.g., "high", "medium", "low")</li>
              <li>Leave optional fields blank if you don't have the information</li>
              <li>Check your CSV encoding - UTF-8 is recommended</li>
              <li>Remove any empty rows at the end of your CSV file</li>
            </ul>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn secondary" onClick={handleClose}>
            {uploadResults ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkImportModal; 